import { Dispatch, SetStateAction, useMemo, useState } from 'react';

import { SongPreview } from '~/interfaces';
import { BottomSheet } from '~/modules/elements/akui/bottom-sheet';
import { Icon } from '~/modules/elements/akui/icon';
import clearString from '~/modules/utils/clear-string';
import groupDigits from '~/modules/utils/group-digits';
import { REMOTE_FILTER_CATEGORIES } from '~/routes/remote-mic/panels/remote-song-list/use-remote-filters';
import {
  CATEGORY_LABELS,
  FilterCategoryId,
} from '~/routes/sing-a-song/song-selection/components/filters/filter-options';
import useFilterCategories, {
  FilterableSong,
  isMultiCategory,
} from '~/routes/sing-a-song/song-selection/components/filters/use-filter-categories';
import { AppliedFilters } from '~/routes/sing-a-song/song-selection/hooks/use-song-list-filter';

interface Props {
  open: boolean;
  onClose: () => void;
  /** Everything the phone's list can reach, which is what the option counts are measured against. */
  catalogue: FilterableSong[];
  /** The whole index - see `useFilterCategories` on why languages are not counted off `catalogue`. */
  languageSource: Pick<SongPreview, 'language'>[];
  resultCount: number;
  filters: AppliedFilters;
  setFilters: Dispatch<SetStateAction<AppliedFilters>>;
}

/**
 * F2's filter surface on the phone, in the phone's own idiom: one sheet behind the toolbar's filter
 * icon, a list of the categories with what each is set to, and the chosen one's options in the same
 * sheet with Back returning to the list. The categories and what picking one does are shared with
 * the TV's panel (`useFilterCategories`), so this is a second surface and not a second answer.
 *
 * Same shape of interaction as the panel it matches: every pick filters the list behind the sheet
 * immediately, there is no Apply, and one Clear all undoes the lot.
 */
export default function RemoteFilterSheet({
  open,
  onClose,
  catalogue,
  languageSource,
  resultCount,
  filters,
  setFilters,
}: Props) {
  const [category, setCategory] = useState<FilterCategoryId | null>(null);
  const [genreSearch, setGenreSearch] = useState('');

  const { optionsFor, isOptionSelected, apply, labelFor, activeFilters, clearAll } = useFilterCategories({
    categories: REMOTE_FILTER_CATEGORIES,
    catalogue,
    languageSource,
    filters,
    setFilters,
  });

  const openCategory = (id: FilterCategoryId) => {
    setGenreSearch('');
    setCategory(id);
  };

  const backToCategories = () => {
    setGenreSearch('');
    setCategory(null);
  };

  const close = () => {
    backToCategories();
    onClose();
  };

  const visibleOptions = useMemo(() => {
    if (!category) return [];
    const options = optionsFor(category);
    if (category !== 'genre' || !genreSearch) return options;
    const wanted = clearString(genreSearch);

    // "Any" always stays reachable, so a search that matches nothing is not a dead end.
    return options.filter((option) => !option.value || clearString(option.label).includes(wanted));
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- optionsFor reads the props this already depends on
  }, [category, genreSearch, catalogue, languageSource, filters]);

  return (
    <BottomSheet
      open={open}
      onClose={close}
      onBack={category ? backToCategories : undefined}
      title={category ? CATEGORY_LABELS[category] : 'Filter'}>
      <div className="flex flex-col gap-2.5" data-test="remote-filters">
        {category === null ? (
          <>
            <span className="-mt-1.5 text-[0.9375rem] text-white/60">{groupDigits(resultCount)} songs</span>
            <div className="flex flex-col overflow-hidden rounded-[1.125rem] bg-white/6 shadow-[inset_0_0_0_1px_rgba(255,255,255,0.12)] [&>*:not(:last-child)]:border-b [&>*:not(:last-child)]:border-white/8">
              {REMOTE_FILTER_CATEGORIES.map((id) => (
                <button
                  key={id}
                  type="button"
                  onClick={() => openCategory(id)}
                  data-test={`filter-${id}`}
                  data-active={activeFilters.includes(id)}
                  className="flex h-15 items-center gap-3 border-none bg-transparent px-4.5 text-left text-white transition-colors duration-150 active:bg-white/6">
                  <span className="min-w-0 flex-1 truncate text-[1.0625rem]">{CATEGORY_LABELS[id]}</span>
                  <span
                    className={`min-w-0 shrink truncate text-[1rem] ${activeFilters.includes(id) ? 'text-active' : 'text-white/50'}`}>
                    {labelFor(id)}
                  </span>
                  <Icon icon="ic:baseline-arrow-right" size={5.5} className="shrink-0 text-white/40" />
                </button>
              ))}
            </div>
            <div className="mt-0.5 flex gap-2.5">
              {/* Only when there is something to undo: left on screen disabled it was the lightest
                  thing on the sheet, which read as the primary action. */}
              {activeFilters.length > 0 && (
                <button
                  type="button"
                  onClick={clearAll}
                  data-test="filters-clear"
                  className="flex h-16 flex-1 cursor-pointer items-center justify-center rounded-[1.125rem] border-none bg-white/8 text-[1.0625rem] text-white shadow-[inset_0_0_0_1px_rgba(255,255,255,0.14)] transition-transform duration-100 active:scale-[0.98]">
                  Clear all
                </button>
              )}
              {/* Not an Apply - every pick has already filtered the list behind this. It is here
                  because a backdrop is not an affordance. */}
              <button
                type="button"
                onClick={close}
                data-test="filters-close"
                className="bg-active flex h-16 flex-1 cursor-pointer items-center justify-center rounded-[1.125rem] border-none text-[1.1875rem] font-bold text-black transition-transform duration-100 active:scale-[0.98]">
                Done
              </button>
            </div>
          </>
        ) : (
          <>
            {/* Said out loud, as on the TV's panel: a list where one row is already filled in does
                not read as one you can go on tapping. */}
            {isMultiCategory(category) && (
              <span className="-mt-1.5 text-[0.9375rem] text-white/60">Tap as many as you like — Any clears them</span>
            )}
            {category === 'genre' && (
              <div className="flex h-13 items-center gap-2.5 rounded-2xl bg-white/8 px-3.5 shadow-[inset_0_0_0_1px_rgba(255,255,255,0.14)]">
                <Icon icon="ic:baseline-search" size={5} className="shrink-0 text-white/50" />
                <input
                  type="text"
                  value={genreSearch}
                  onChange={(event) => setGenreSearch(event.target.value)}
                  placeholder="Find a genre…"
                  data-test="filter-genre-search"
                  className="h-full min-w-0 flex-1 border-none bg-transparent text-[1rem] text-white outline-none"
                />
              </div>
            )}
            {visibleOptions.map((option) => {
              const selected = isOptionSelected(category, option.value);

              return (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => apply(category, option.value)}
                  // `isE2E()` suppresses focus fills, so what is picked is said on an attribute
                  // rather than only in colour.
                  data-test={`filter-${category}-option-${option.value}`}
                  data-selected={selected}
                  className={`flex h-15 items-center gap-3 rounded-2xl border-none px-4.5 text-left transition-colors duration-150 ${
                    selected
                      ? 'bg-active font-bold text-black'
                      : 'bg-white/6 text-white shadow-[inset_0_0_0_1px_rgba(255,255,255,0.12)]'
                  }`}>
                  <span className="min-w-0 flex-1 truncate text-[1.0625rem]">{option.label}</span>
                  {option.count !== undefined && (
                    <span className="shrink-0 text-[0.9375rem] opacity-60">{groupDigits(option.count)}</span>
                  )}
                </button>
              );
            })}
          </>
        )}
      </div>
    </BottomSheet>
  );
}
